import { ReactComponent as AddSvg } from "@/src/assets/icons/icon-add-white.svg";
import DownloadGraySvg from "@/src/assets/icons/icon-download-gray.svg";
import DownloadSvg from "@/src/assets/icons/icon-download-indigo.svg";
import InfoSvg from "@/src/assets/icons/icon-info-gray.svg";
import PreviewFileButton from "@/src/components/atom/PreviewFileButton";
import { Button, IconButton } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import { renderNoRowsComponent } from "@/src/components/atom/Table";
import ReadOnlyText from "@/src/components/atom/TextArea/ReadOnlyText";
import Typo from "@/src/components/atom/Typo";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import AddMemoDialog from "@/src/components/organism/AddMemoDialog";
import useAlert from "@/src/hooks/useAlert";
import {
  contractDetailApi,
  useCreateExporterContractMemoMutation,
} from "@/src/store/apis/contracts/contractDetail";
import { ContractDetailViewDto } from "@/src/store/apis/contracts/contractDetail/interface";
import colorSet from "@/src/styles/color";
import { StyledScroll } from "@/src/styles/scroll";
import { downloadFile } from "@/src/utils/downloadFile";
import { isNull, isUndefined } from "@/src/utils/is";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { styled } from "styled-components";

export const PAYMENT_TERMS_LC_TYPE = "L/C";
const CONTRACT_INFO_DATE_FORMAT = "YYYY-MM-DD";

const memoEmptyArray: ContractDetailViewDto["simpleContractMemos"] = [];

enum DialogState {
  NULL,
  ADD_MEMO,
}

interface ContractInformationCardProps {
  isEditAuth: boolean;
}

function ContractInformationCard({ isEditAuth }: ContractInformationCardProps) {
  const { t } = useTranslation();
  const params = useParams();
  const alert = useAlert();
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);

  // API
  const [createMemo, { isLoading: isCreateMemoLoading }] =
    useCreateExporterContractMemoMutation();

  const {
    paymentTerms,
    paymentTermsRemark,
    orderDateAt,
    expireDateAt,
    lastShipmentDateAt,
    placeOfDelivery,
    anyPortIn,
    origin,
    allocation,
    poNo,
    poSimpleMedia,
    scNo,
    scSimpleMedia,
    paymentTermsSimpleMedia,
    hsCode,
    freetime,
    isAddContractTerms,
    isAddBankDetail,
    contractRemark,
    importerContractRemark,
    simpleContractMemos,
    lcNo,
    portOfDischarge,
    contractData,
  } = contractDetailApi.endpoints.getContractDetail.useQueryState(
    {
      id: Number(params.id),
    },
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isUndefined(currentData) || isError || isFetching;

        return {
          paymentTerms: !isUnstable ? currentData.paymentTerms : "",
          paymentTermsRemark: !isUnstable
            ? (currentData.paymentTermsRemark ?? "")
            : "",
          orderDateAt:
            !isUnstable && currentData.orderDateAt
              ? dayjs(currentData.orderDateAt).format(CONTRACT_INFO_DATE_FORMAT)
              : "-",
          expireDateAt:
            !isUnstable && currentData.expireDateAt
              ? dayjs(currentData.expireDateAt).format(
                  CONTRACT_INFO_DATE_FORMAT,
                )
              : "-",
          lastShipmentDateAt:
            !isUnstable && currentData.lastShipmentDateAt
              ? dayjs(currentData?.lastShipmentDateAt).format(
                  CONTRACT_INFO_DATE_FORMAT,
                )
              : "-",
          placeOfDelivery: !isUnstable
            ? (currentData.placeOfDelivery ?? "-")
            : "-",
          anyPortIn: !isUnstable ? (currentData.anyPortIn ?? "") : "",
          origin: !isUnstable ? (currentData.origin ?? "-") : "-",
          allocation: currentData?.allocation,
          poNo: !isUnstable ? (currentData.poNo ?? "-") : "-",
          poSimpleMedia: currentData?.poSimpleMedia,
          scNo: !isUnstable ? (currentData.scNo ?? "-") : "-",
          scSimpleMedia: currentData?.scSimpleMedia,
          paymentTermsSimpleMedia: currentData?.paymentTermsSimpleMedia,
          hsCode: !isUnstable
            ? currentData.hsCode || (currentData as any).importerHsCode || "-"
            : "-",
          freetime: !isUnstable ? (currentData.freetime ?? "-") : "-",
          isAddContractTerms:
            !isUnstable && currentData?.isAddContractTerms
              ? t("common:included")
              : t("common:notIncluded"),
          isAddBankDetail:
            !isUnstable && currentData?.isAddBankDetail
              ? t("common:included")
              : t("common:notIncluded"),
          contractRemark: !isUnstable
            ? (currentData.contractRemark ?? "-")
            : "-",
          importerContractRemark: !isUnstable
            ? (currentData.importerContractRemark ?? "-")
            : "-",
          simpleContractMemos:
            !isUnstable && currentData.simpleContractMemos
              ? (currentData.simpleContractMemos ?? memoEmptyArray)
              : memoEmptyArray,
          lcNo: !isUnstable ? (currentData.lcNo ?? "-") : "-",
          portOfDischarge: !isUnstable
            ? currentData.portOfDischarge || "-"
            : "-",
          contractData: currentData,
        };
      },
    },
  );

  const handleCreateMemoClick = async (memo: string) => {
    try {
      await createMemo({ id: Number(params.id), memo }).unwrap();
      setDialogState(DialogState.NULL);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const renderDialog = () => {
    if (dialogState === DialogState.ADD_MEMO) {
      return (
        <AddMemoDialog
          open
          onOpenChange={() => setDialogState(DialogState.NULL)}
          onSave={handleCreateMemoClick}
          isLoading={isCreateMemoLoading}
        />
      );
    }
  };

  return (
    <ContractInformationCardContainer>
      {/* Left */}
      <FlexColumn>
        {/* Contract Information */}
        <StyledSectionCardFlexFive>
          <ContractInformationFlexColumn>
            {/* Top Section - 2 Column Grid */}
            <SectionGridContainer>
              {/* Left Column - Location */}
              <ContractInformationSection>
                <Typo as="p" typoType="b9m" color="gray7">
                  {t("contract:exporter.detail.label.location")}
                </Typo>
                <section>
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.placeOfDelivery")}
                    value={placeOfDelivery ?? anyPortIn ?? "-"}
                  />
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.portOfDischarge")}
                    value={portOfDischarge}
                  />
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.originLocation")}
                    value={origin}
                  />
                  <SectionCardRow
                    label={t("common:freetime")}
                    value={freetime}
                  />
                </section>
              </ContractInformationSection>

              {/* Right Column - Attachment Files */}
              <ContractInformationSection>
                <Typo as="p" typoType="b9m" color="gray7">
                  {t("contract:exporter.detail.label.attachmentFile")}
                </Typo>
                <section>
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.poNo")}
                    value={
                      <FileInfoContainer>
                        <BreakWordTypo typoType="b7r" color="gray5">
                          {poSimpleMedia ? poSimpleMedia.originalFileName : "-"}
                        </BreakWordTypo>
                        <div>
                          <IconButton
                            buttonSize={24}
                            buttonColor="blue"
                            buttonGrade="secondary"
                            disabled={isNull(poSimpleMedia)}
                          >
                            {isNull(poSimpleMedia) ? (
                              <Icon iconSrc={DownloadGraySvg} iconSize={16} />
                            ) : (
                              <Icon
                                iconSrc={DownloadSvg}
                                iconSize={16}
                                onClick={() =>
                                  downloadFile(
                                    poSimpleMedia?.mediaUrl ?? "",
                                    poSimpleMedia?.originalFileName ?? "",
                                  )
                                }
                              />
                            )}
                          </IconButton>
                          <PreviewFileButton mediaUrl={poSimpleMedia?.mediaUrl} />
                        </div>
                      </FileInfoContainer>
                    }
                  />

                  <SectionCardRow
                    label={t("contract:exporter.detail.label.scNo")}
                    value={
                      <FileInfoContainer>
                        <BreakWordTypo typoType="b7r" color="gray5">
                          {scSimpleMedia ? scSimpleMedia.originalFileName : "-"}
                        </BreakWordTypo>
                        <div>
                          <IconButton
                            buttonSize={24}
                            buttonColor="blue"
                            buttonGrade="secondary"
                            disabled={isNull(scSimpleMedia)}
                          >
                            {isNull(scSimpleMedia) ? (
                              <Icon iconSrc={DownloadGraySvg} iconSize={16} />
                            ) : (
                              <Icon
                                iconSrc={DownloadSvg}
                                iconSize={16}
                                onClick={() =>
                                  downloadFile(
                                    scSimpleMedia?.mediaUrl ?? "",
                                    scSimpleMedia?.originalFileName ?? "",
                                  )
                                }
                              />
                            )}
                          </IconButton>
                          <PreviewFileButton mediaUrl={scSimpleMedia?.mediaUrl} />
                        </div>
                      </FileInfoContainer>
                    }
                  />

                  <SectionCardRow
                    label={t("contract:exporter.detail.label.lcNo")}
                    value={
                      <FileInfoContainer>
                        <BreakWordTypo typoType="b7r" color="gray5">
                          {paymentTermsSimpleMedia ? paymentTermsSimpleMedia.originalFileName : "-"}
                        </BreakWordTypo>
                        <div>
                          <IconButton
                            buttonSize={24}
                            buttonColor="blue"
                            buttonGrade="secondary"
                            disabled={isNull(paymentTermsSimpleMedia)}
                          >
                            {paymentTermsSimpleMedia === null ? (
                              <Icon iconSrc={DownloadGraySvg} iconSize={16} />
                            ) : (
                              <Icon
                                iconSrc={DownloadSvg}
                                iconSize={16}
                                onClick={() =>
                                  downloadFile(
                                    paymentTermsSimpleMedia?.mediaUrl ?? "",
                                    paymentTermsSimpleMedia?.originalFileName ?? "",
                                  )
                                }
                              />
                            )}
                          </IconButton>
                          <PreviewFileButton mediaUrl={paymentTermsSimpleMedia?.mediaUrl} />
                        </div>
                      </FileInfoContainer>
                    }
                  />
                </section>
              </ContractInformationSection>
            </SectionGridContainer>

            {/* Divider */}
            <SectionDivider />

            {/* Bottom Section - 2 Column Grid */}
            <SectionGridContainer>
              {/* Left Column - ETC */}
              <ContractInformationSection>
                <Typo as="p" typoType="b9m" color="gray7">
                  {t("contract:exporter.detail.label.etc")}
                </Typo>
                <section>
                  <SectionCardRow label={t("common:hsCode")} value={hsCode} />
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.poNo")}
                    value={poNo}
                  />
                </section>
              </ContractInformationSection>

              {/* Right Column - Optional */}
              <ContractInformationSection>
                <Typo as="p" typoType="b9m" color="gray7">
                  {t("contract:exporter.detail.label.optional")}
                </Typo>
                <section>
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.contractTerm")}
                    value={isAddContractTerms}
                  />
                  <SectionCardRow
                    label={t("contract:exporter.detail.label.bankDetail")}
                    value={isAddBankDetail}
                  />
                </section>
              </ContractInformationSection>
            </SectionGridContainer>
          </ContractInformationFlexColumn>
        </StyledSectionCardFlexFive>
        {/* Remark */}
        <div style={{ display: "flex", gap: "16px" }}>
          <StyledSectionCardFlexOne cardTitle={t("common:remark")}>
            <RemarkContainer>
              <SectionCardRow
                label={
                  <Typo color="gray7" typoType="b9m">
                    {t("contract:exporter.detail.label.buyerRemark")}
                  </Typo>
                }
                direction="vertical"
                value={
                  <ReadOnlyText typoType="b7r">
                    {importerContractRemark}
                  </ReadOnlyText>
                }
              />

              <SectionCardRow
                label={
                  <Typo color="gray7" typoType="b9m">
                    {t("contract:exporter.detail.label.sellerRemark")}
                  </Typo>
                }
                direction="vertical"
                value={
                  <ReadOnlyText typoType="b7r">{contractRemark}</ReadOnlyText>
                }
              />
            </RemarkContainer>
          </StyledSectionCardFlexOne>

          {/* Memo */}
          <StyledSectionCardFlexOne
            cardTitle={t(
              "contract:exporter.detail.contractInformationCard.memo",
            )}
            rightAccessory={
              <StyledButton
                buttonSize={32}
                buttonColor="black"
                buttonGrade="tertiary"
                onClick={() => setDialogState(DialogState.ADD_MEMO)}
                disabled={!isEditAuth}
              >
                <AddIcon data-disabled={!isEditAuth} />
                {t("sales:detail.button.add")}
              </StyledButton>
            }
          >
            <MemoContainer>
              <MemoDescription>
                <StyledIcon iconSrc={InfoSvg} />
                <Typo typoType="b9r" color="gray6">
                  {t("contract:exporter.detail.description.memo")}
                </Typo>
              </MemoDescription>

              <MemoContent>
                {simpleContractMemos.length >= 1 ? (
                  simpleContractMemos.map((item, idx) => {
                    return (
                      <MemoItem key={idx.toString()}>
                        <MemoLabelContainer>
                          <Typo color="gray3" typoType="h9">
                            {item.writerName}
                          </Typo>
                          <Typo color="gray7" typoType="b9r">
                            {`(${dayjs(item.wroteAt).format("YY-MM-DD")}, ${dayjs(
                              item.wroteAt,
                            ).format("HH:mm")})`}
                          </Typo>
                        </MemoLabelContainer>

                        <ReadOnlyText typoType="b7r" color="gray6">
                          {item.memo}
                        </ReadOnlyText>
                      </MemoItem>
                    );
                  })
                ) : (
                  <MemoNoDataContainer>
                    {renderNoRowsComponent()}
                  </MemoNoDataContainer>
                )}
              </MemoContent>
            </MemoContainer>
          </StyledSectionCardFlexOne>
        </div>
      </FlexColumn>
      {renderDialog()}
    </ContractInformationCardContainer>
  );
}

export default ContractInformationCard;

const ContractInformationCardContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  height: 100%;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

const StyledSectionCardFlexOne = styled(SectionCard)`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledSectionCardFlexFive = styled(StyledSectionCardFlexOne)`
  flex: 5;
`;

const ContractInformationFlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ContractInformationRow = styled.div`
  & > .section-label {
    padding-bottom: 8px;
  }

  & > section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const SectionGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;
`;

const ContractInformationSection = styled.div`
  & > .section-label {
    padding-bottom: 8px;
  }

  & > section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colorSet.gray10};
`;

const ContractDateContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  background: ${colorSet.gray11};
  padding: 16px;
  border-radius: 8px;
  gap: 16px;
`;

const ContractDateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const GrayLine = styled.div`
  grid-column: 2 span;
  border-bottom: 1px solid ${colorSet.gray10};
`;

const RemarkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
  height: 400px;
  ${StyledScroll}
`;

const MemoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MemoDescription = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const MemoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
  height: 400px;
  ${StyledScroll}
`;

const MemoLabelContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
`;

const MemoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MemoNoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const FileInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  & > div {
    display: flex;
    gap: 4px;
  }

  a {
    line-height: 0px;
  }
`;

const PaymentTermsContainer = styled.div`
  text-align: center;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AddIcon = styled(AddSvg)`
  width: 16px;
  height: 16px;

  path {
    fill: ${colorSet.gray2};
  }

  &[data-disabled="true"] {
    path {
      fill: ${colorSet.gray8};
    }
  }
`;
